import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCheck, ClipboardList } from "lucide-react";

const ago = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
};

export function NotificationBell() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/notifications").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = () => api.post("/notifications/read", { all: true }).then(load).catch(() => {});
  const openItem = (n) => {
    if (!n.read) api.post("/notifications/read", { ids: [n.id] }).then(load).catch(() => {});
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-8 w-8 inline-flex items-center justify-center rounded-md border hover:bg-secondary transition-colors duration-200" aria-label="Notifications" data-testid="notif-bell">
          <Bell size={15} />
          {data.unread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#BB162B] text-white text-[10px] font-bold flex items-center justify-center num" data-testid="notif-badge">{data.unread > 99 ? "99+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-heading font-semibold">Notifications {data.unread > 0 && <span className="text-xs text-muted-foreground font-normal">· {data.unread} unread</span>}</span>
          {data.unread > 0 && <button onClick={markAll} className="text-[11px] inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="notif-mark-all"><CheckCheck size={12} /> Mark all read</button>}
        </div>
        <ul className="max-h-[60vh] overflow-y-auto divide-y" data-testid="notif-list">
          {data.items.length === 0 && <li className="px-3 py-6 text-xs text-muted-foreground text-center" data-testid="notif-empty">Nothing yet — plan revisions made after the window closes will appear here.</li>}
          {data.items.map((n) => (
            <li key={n.id}>
              <button onClick={() => openItem(n)} className={`w-full text-left px-3 py-2 hover:bg-secondary/70 transition-colors duration-200 ${n.read ? "" : "bg-amber-50/70"}`} data-testid={`notif-item-${n.id}`}>
                <div className="flex items-start gap-2">
                  <ClipboardList size={14} className={`mt-0.5 shrink-0 ${n.read ? "text-muted-foreground" : "text-[#BB162B]"}`} />
                  <div className="min-w-0 flex-1">
                    <div className={`text-xs leading-snug ${n.read ? "font-medium" : "font-bold"}`}>{n.title}</div>
                    {n.body && <div className="text-[11px] text-muted-foreground mt-0.5 num">{n.body}</div>}
                    <div className="text-[10px] text-muted-foreground mt-0.5">{n.actor} · {ago(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#BB162B] shrink-0" />}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
